<template>
    <div :class="cssClasses">
        <div class="l-ss-c-search-input-node-wrapper">
            <app-input
                v-model="value"
                :show-search-icon="showActions && !$screen.isMobile"
                clearable
                :placeholder="placeHolder"
                autocomplete="searchbooster"
                type="text"
                @focus="onFocus"
                @blur="onBlur"
                @clear="resetQuery"
                @input.native="onInput"
                @click="onClick"
                @keydown="onKeyDown"
                @mousedown="onMouseDown"
                @search="handleSearch"
                @hook:mounted="initStickyInput"
                ref="input">
                <template #prefix>
                    <slot name="prefix"></slot>
                </template>

                <template #content>
                    <div
                        class="l-ss-c-search-input-image-container"
                        v-if="image">
                        <img
                            class="l-ss-c-search-input-image"
                            :src="image">

                        <span class="l-ss-c-search-input-image-text">
                            {{ $t('imageSearch.searchResults') }}
                        </span>

                        <delete-btn @click="resetQuery($event, true)"/>
                    </div>
                </template>

                <template
                    #suffix
                    v-if="showInputActions">
                    <div
                        class="l-ss-c-search-input-top-actions">
                        <button
                            v-if="enableImageSearch"
                            class="l-ss-c-search-input-icon l-ss-c-search-input-icon-image"
                            :disabled="isLoading"
                            @click="toggleImagePicker">
                            <i v-html="iconImage"></i>
                        </button>
                        <button
                            v-if="enableVoiceSearch"
                            class="l-ss-c-search-input-icon l-ss-c-search-input-icon-voice"
                            :disabled="isLoading"
                            @click="toggleVoiceSearch">
                            <i v-html="iconVoice"></i>
                        </button>
                    </div>
                </template>
            </app-input>

            <transition name="completions">
                <completions-wrapper
                    v-if="showCompletions"
                    :completions="items"
                    :taps-complitions="tapsCompletions"
                    :is-cart-available="isCartAvailable"
                    :query="searchState.public.query"
                    :is-show-signature="isShowSignature"
                    class="l-ss-c-search-input-completions"
                    ref="completions"
                    v-on="completionsWrapperEventListeners"/>
            </transition>
        </div>
    </div>
</template>

<script>
    import RootComponentMixin from '../../mixins/root-component';
    import CompletionsMixin from '../../mixins/completions';
    import CompletionsMethods from '../../mixins/completions-methods';
    import EventEmitterMixin from '../../mixins/event-emitter';
    import { commonOfferEventHandlers, mapHandlers } from '../../mixins/event-emitter';

    import CompletionsWrapper from '../completions/completions-wrapper.vue';
    import AppInput from '@/core/components/common/app-input.vue';
    import DeleteBtn from '@/core/components/delete-btn';

    import imageIcon from '../../assets/images/icons/gallery.svg';
    import voiceIcon from '../../assets/images/icons/microphone.svg';

    import useStickyInput from '@/core/components/search-input/use-sticky-input';

    import getEventTarget from '../../helpers/event-target';

    export default {
        components: {
            AppInput,
            CompletionsWrapper,
            DeleteBtn,
        },
        mixins: [
            CompletionsMixin,
            RootComponentMixin,
            CompletionsMethods,
            EventEmitterMixin,
        ],
        props: {
            query: {
                type: String,
                default: '',
            },
            showActions: {
                type: Boolean,
                default: true,
            },
            enableImageSearch: {
                type: Boolean,
                default: false,
            },
            enableVoiceSearch: {
                type: Boolean,
                default: false,
            },
            isCartAvailable: {
                type: Boolean,
                default: false,
            },
            isLoading: {
                type: Boolean,
                default: false,
            },
            image: {
                type: String,
            },
        },
        data() {
            return {
                timer: null,
                value: this.query || '',
                isFocused: false,
                isMounted: false,
                microphonePermission: true,
                items: [],
                lastQuery: '',
                inputFocus: true,
                isUserFocusInit: false,
                downKeys: {
                    enter: 'Enter',
                    arrowUp: 'ArrowUp',
                    arrowDown: 'ArrowDown',
                },
                closeCompletionsTime: 100,
                eventInput: null,
            };
        },
        computed: {
            placeHolder() {
                return (!this.$screen.isMobile && (this.enableImageSearch && !this.value)) ?
                    this.$t('searchInput.placeholderImage') :
                    this.$t('searchInput.placeholder');
            },
            cssClasses() {
                const classes = {
                    'l-ss-c-search-input': true,
                    'l-ss-c-search-input-is-focused': Boolean(this.isFocused),
                };
                return { ...this.wrapperClasses, ...classes };
            },
            iconImage() {
                return imageIcon;
            },
            iconVoice() {
                return voiceIcon;
            },
            showInputActions() {
                if (!this.showActions) {
                    return false;
                }

                if (this.$screen.isMobile) {
                    return !this.value;
                }

                return true;
            },
            completionsWrapperEventListeners() {
                return {
                    ...mapHandlers(this, commonOfferEventHandlers),
                    'click': this.handleClick,
                    'search-input-change': this.emitSearchInputChange,
                    'handle-search': this.emitHandleSearch,
                    'handle-tags-click': (tag) => this.$emit('change' , tag),
                    'preview-tab-click': this.emitPreviewTabClick,
                    'search-all': this.emitSearchAll,
                    'preview-show-all-click': this.emitPreviewShowAllClick,
                    'remove-history': this.emitRemoveHistory,
                    'reset-query': this.emitResetQuery,
                };
            },
        },
        watch: {
            query(value) {
                this.value = value;
            },
            'store.state.ui.isCompletionsVisible': {
                handler(value) {
                    if (value && !this.isFocused && this.value) {
                        this.$refs.input.focus();
                        this.setIsFocused(true);
                    }
                },
            },
            'store.state.ui.isCategorySearch': {
                handler() {
                    this.clearSearchState();
                },
            },
            completionsItems() {
                if (this.completionsItems.length) {
                    this.items = this.completionsItems;
                }
            },
            showCompletions(value) {
                if (value) {
                    document.body.addEventListener('click', this.clickOutsideComplitions, true);
                    this.$emit('completions-open', this.items);
                } else if (value === false) {
                    this.$emit('completions-close');
                }
            },
            value(newValue, oldValue) {
                if (newValue === '' && oldValue !== '') {
                    setTimeout(() => {
                        if (this.eventInput) {
                            this.deleteAllTextEventClick('deleteAllText', this.eventInput, oldValue);
                        }
                    });
                }
            },
        },
        mounted() {
            this.isMounted = true;

            if (navigator && navigator.permissions) {
                navigator.permissions.query({ name: 'microphone' }).then((permissionStatus) => {
                    this.microphonePermission = permissionStatus.state !== 'denied';
                    if (this.microphonePermission === false) {
                        console.warn('Microphone permission is not granted, voice search is disabled!');
                    }
                }).catch((error) => {
                    if (this.$configuration.debug) {
                        console.error('Can\'t query permissions', error);
                    }
                    return false;
                });
            }
            window.addEventListener(
                'touchstart',
                (event) => {
                    const target = getEventTarget(event);
                    if (target.nodeName.toLowerCase() !== 'input') {
                        this.$refs.input?.blur();
                    }
                },
                false,
            );
            if (!this.$configuration.isPopup) {
                window.addEventListener(
                    'click',
                    (event) => {
                        this.completionsClose(event);
                    },
                    false,
                );
            }
        },
        methods: {
            clearSearchState() {
                this.instance.controller.searchStateManager.clearFromStorage();
                this.instance.controller.clearSearchCache();
                this.instance.$anchors.forEach(anchor => anchor.value = '');
            },
            clickOutsideComplitions(event) {
                if (this.showCompletions) {
                    const path = event.path || (event.composedPath && event.composedPath());
                    if (this.$refs.completions?.$el.contains(path[0])) {
                        return;
                    }
                    this.completionsClose(event);
                } else {
                    document.body.removeEventListener('click', this.clickOutsideComplitions, true);
                }
            },
            toggleVoiceSearch(event) {
                const VOICE_SEARCH_STATE_CHECK_DELAY = 250;
                const alertPermissions = () => {
                    alert(this.$t('voiceSearchIsDenied'));
                };
                if (this.microphonePermission) {
                    this.$emit('toggle-voice-search', event);
                    setTimeout(() => {
                        if (this.store.state.ui.isVoiceSearchActive === false) {
                            alertPermissions();
                        }
                    }, VOICE_SEARCH_STATE_CHECK_DELAY);
                } else {
                    alertPermissions();
                }
            },
            handleShowCompletions() {
                if (!this.store.state.ui.isFiltersOpened && !this.store.state.ui.isImageContours) {
                    this.store.commit('ui/SET_PROPERTY', { key: 'isCompletionsVisible', value: true });
                }
            },
            initStickyInput() {
                if (this.$screen.isMobile) {
                    useStickyInput(this.$root);
                }
            },
            onClick() {
                this.store.commit('ui/SET_PROPERTY', { key: 'isCompletionsVisible', value: true });
            },
            onFocus(event) {
                this.setIsFocused(true);
                if (this.isUserFocusInit) {
                    this.handleShowCompletions();
                }
                this.$emit('focus', event);
                if (this.$configuration.completions.isShowAfterInputFieldFocus) {
                    this.onClick();
                }

                this.isUserFocusInit = false;
            },
            onKeyDown(event) {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    this.$emit('handle-search');
                    event.target.blur();
                    this.searchClickEvent('enterClick', event);
                }
                if (!this.completionsItems.length) return;
                const find = this.completionsItems.findIndex((el) => el.active === 1);
                if (event.code === this.downKeys.enter) {
                    this.onKeyDownEnter(event, find);
                } else if (event.code === this.downKeys.arrowUp || event.code === this.downKeys.arrowDown) {
                    event.preventDefault();

                    /* Фокусим input если список достиг конца круга (нулевого или последнего элемента) */
                    this.inputFocus = this.onKeyDownInputFocus(event, find);

                    /* Выставляем active = 1 следующему элементу, вниз + 1, вверх - 1, если за ним не следует сам input  */
                    if (!this.inputFocus) {
                        this.onKeyDownNextElement(event, find);
                    }
                }
            },
            onMouseDown() {
                this.isUserFocusInit = true;
            },
            completionsClose(event) {
                const path = event.path;
                const target = getEventTarget(event);
                const nodeName = event.target.nodeName;
                if (nodeName !== 'SEARCHBOOSTER-SEARCH-POPUP') {
                    this.setIsFocused(false);
                    this.store.commit('ui/SET_PROPERTY', { key: 'isCompletionsVisible', value: false });
                } else {
                    if (target.nodeName.toLowerCase() !== 'input') {
                        const find = path?.find(el => el.className?.includes('-input-completions'));
                        if (!find) {
                            this.setIsFocused(false);
                            this.store.commit('ui/SET_PROPERTY', { key: 'isCompletionsVisible', value: false });
                        }
                    }
                }
            },
            onBlur(event) {
                // Hide completions only if we in current window [ignore tab switching and so on] and it's not mobile device
                this.$emit('blur', event);
            },
            onInput(event) {
                const isFunctionalKey = /shift|control|back|alt|lock/i.test(event.code);
                this.lastQuery = this.value;
                this.eventInput = event;
                if (isFunctionalKey === false) {
                    this.$emit('change', event.target.value);
                }
                this.$emit('search-input', event.target.value);
            },
            resetQuery(event, resetByImage = false) {
                if (!resetByImage) {
                    this.$refs?.input?.focus();
                    this.deleteAllTextEventClick('buttonTextClearClick', event, this.value);
                }
                
                this.clearSearchState();
                this.$emit('reset-query');

                if (resetByImage) {
                    this.$nextTick(() => {
                        this.$refs?.input?.focus();
                        this.deleteAllTextEventClick('buttonTextClearClick', event, '');
                    });
                }
            },
            deleteAllTextEventClick(clickType, event, value) {
                this.emitDeleteAllText({
                    type: 'deleteAllText',
                    query: value,
                    event,
                    customEventType: clickType,
                });
            },
            handleCompletionSearch(completion) {
                if (!completion.search.query) {
                    this.emitParamsSearch(completion);
                    return;
                }
                if (!completion.url) {
                    this.emitAutocompleteSearch(
                        completion.text.value || completion.text.html || completion.text.label,
                    );
                }
            },
            handleClick(data) {
                let lockSearch = this.$configuration.handleClick(data);
                this.store.commit('ui/SET_PROPERTY', { key: 'isComplitionSearch', value: true });
                this.completionClickHandler(data, (completion) => {
                    completion.query = this.value;
                    if (lockSearch === false) return;
                    this.handleCompletionSearch(completion);
                });
            },
            onKeyDownEnter(event, find) {
                if (!this.value) {
                    return;
                }

                /* Если найден активный элемент - проверяем наличие url, отменяем все действия, и переходим по ссылке  */
                if (find !== -1) {
                    const completion = this.items.find((el) => el.active === 1);
                    if ('url' in completion) {
                        event.preventDefault();
                        event.stopPropagation();
                        this.completionClickHandler({ event, completion });
                        return;
                    }
                }

                this.$refs.input.blur();
                this.$emit('keyDownEnter', event);
            },
            onKeyDownInputFocus(event, find) {
                const completionsLength = this.completionsItems.length;
                if (completionsLength === 0) {
                    return;
                }
                const lastIndex = completionsLength - 1;

                if (
                    ((find === lastIndex && event.code === this.downKeys.arrowDown) ||
                        (find === 0 && event.code === this.downKeys.arrowUp)) &&
                    !this.inputFocus
                ) {
                    this.value = this.lastQuery;
                    this.items.forEach((el) => (el.active = 0));
                    return true;
                } else {
                    return false;
                }
            },
            onKeyDownNextElement(event, find) {
                let nextIndex;
                this.completionsItems.at(find).active = 0;
                if (event.code === this.downKeys.arrowDown) {
                    nextIndex = find + 1;
                } else {
                    if (find === -1) {
                        nextIndex = find;
                    } else {
                        nextIndex = find - 1;
                    }
                }
                this.completionsItems.at(nextIndex).active = 1;
                this.value = this.completionsItems.at(nextIndex).text.value;
            },

            toggleImagePicker(event) {
                this.$emit('toggle-image-picker');
                this.store.commit('ui/SET_PROPERTY', { key: 'isNameWidgetActive', value: this.$root.$options.customElement.nodeName });
                this.emitButtonImageClick(event);
            },

            handleSearch(event) {
                if (this.isLoading) {
                    return;
                }

                this.emitHandleSearch();
                this.emitSearchClick({
                    type: 'searchClick',
                    query: this.value,
                    event,
                    customEventType: 'buttonClick',
                });
            },

            searchClickEvent(clickType, event) {
                this.$emit('search-click', {
                    type: 'searchClick',
                    query: this.value,
                    event,
                    customEventType: clickType,
                });
            },

            setIsFocused(value) {
                this.isFocused = value;
            },
        },
    };
</script>
